import React, { useEffect, useState } from 'react';
import StreamingLogViewer from './StreamingLogViewer';
import './StreamingClients.css';

// ONE STREAMING CLIENT: what is deployed (Kubernetes' live answer), the settings it is deployed with, Deploy / Redeploy,
// Stop, and its log. The caller supplies the actions, so the same panel serves Admin › Infra › Streaming Clients (the
// admin API) and the pipeline board's Deploy Streaming Client (the pipelines API, which relays to the same lambda).
//
//   status    { key, client, deployment, clusterError } — the shape both APIs answer with
//   onDeploy  async (settings) => newStatus     onStop async () => void (omit to hide Stop)
//   showLog   offer the container log (admin API; superadmin)

export const STATE_LABEL = {
    running: 'Running', starting: 'Starting', failing: 'Failing', stopped: 'Stopped',
};
export const STATE_CLASS = { running: 'returned', starting: 'queued', failing: 'error', stopped: 'queued' };

export const stateOf = (row) => {
    if (!row.deployment) return { label: 'Not deployed', cls: 'queued' };
    const s = row.deployment.state;
    return { label: STATE_LABEL[s] || s, cls: STATE_CLASS[s] || 'queued' };
};

export const KIND_LABEL = { pipeline: 'Pipeline', sync: 'Sync', orphan: 'Orphan', other: 'Other' };

const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const errText = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;
const LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error'];

export default function StreamingClientPanel({ status, onDeploy, onStop, showLog = true }) {
    const client = status.client;
    const dep = status.deployment;
    const managed = !!client;          // a pl-/sp- candidate: has settings and can be (re)deployed with them
    const [form, setForm] = useState({ logLevel: 'info' });
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [logOpen, setLogOpen] = useState(false);

    useEffect(() => {
        const s = (client && client.settings) || {};
        setForm({ logLevel: s.logLevel || 'info' });
    }, [client]);

    const run = async (what, fn) => {
        setBusy(what);
        setError(null);
        try { await fn(); } catch (e) { setError(errText(e)); } finally { setBusy(null); }
    };

    const st = stateOf(status);
    const r = status.resolved;          // present on a single client's status (GET one, deploy, save)
    const s = (client && client.settings) || {};

    return (
        <div className="stc-panel">
            <div className="stc-grid">
                <div><div className="scq-meta">Status</div><span className={`scq-status scq-status-${st.cls}`}>{st.label}</span>
                    {dep && dep.reason && <div className="scq-meta">{dep.reason}</div>}</div>
                <div><div className="scq-meta">Deployment</div><span className="scq-mono">{dep ? `${dep.namespace}/${dep.deployment}` : '—'}</span></div>
                <div><div className="scq-meta">Deployed</div>{dep ? when(dep.deployedAt) : '—'}{dep && dep.deployedBy && <div className="scq-meta">{dep.deployedBy}</div>}</div>
                <div><div className="scq-meta">Pod</div>{dep && dep.pod ? <><span className="scq-mono">{dep.pod}</span><div className="scq-meta">{dep.podPhase}{dep.startedAt ? ` · up since ${when(dep.startedAt)}` : ''}</div></> : '—'}</div>
                <div><div className="scq-meta">Restarts</div>{dep ? dep.restarts : '—'}
                    {dep && dep.lastTermination && <div className="scq-meta">last: {dep.lastTermination.reason} ({dep.lastTermination.exitCode}) {when(dep.lastTermination.finishedAt)}</div>}</div>
                <div><div className="scq-meta">Image</div><span className="scq-mono">{dep ? (dep.image || '').split('/').pop() : '—'}</span></div>
            </div>

            {status.clusterError && <div className="os-msg os-msg-error">The cluster could not be read: {status.clusterError}</div>}
            {s.lastError && <div className="os-msg os-msg-error">Last {s.lastAction || 'action'} failed ({when(s.lastActionAt)}): {s.lastError}</div>}
            {s.lastAction && !s.lastError && <div className="scq-meta">Last {s.lastAction} {when(s.lastActionAt)}{s.lastActionBy ? ` by ${s.lastActionBy}` : ''}</div>}

            {/* WHAT A DEPLOY WILL USE — the credential chosen for the source when the implementation was added (or the
                sync project's source connector), and each value with where it came from. Secrets only as set / not. */}
            {managed && r && (
                <div className="stc-uses">
                    <div className="stc-uses-title">Deploys with</div>
                    <div className="stc-grid">
                        <div><div className="scq-meta">Credential</div>
                            {r.credentialName || (r.connectorId ? `Connector ${r.connectorId}` : '—')}
                            {r.credentialId && <span className="scq-meta"> #{r.credentialId}</span>}
                            <div className="scq-meta">{r.credentialFrom}</div></div>
                        {r.gatewayUrl !== undefined && <div><div className="scq-meta">Gateway</div><span className="scq-mono">{r.gatewayUrl || '—'}</span></div>}
                        {r.authMethod && <div><div className="scq-meta">Sign-in</div>{r.authMethod}{r.enterpriseId ? ` · enterprise ${r.enterpriseId}` : ''}</div>}
                        <div><div className="scq-meta">Chain</div>{r.chainCode || <span style={{ color: '#b91c1c' }}>none</span>}
                            {r.chainFrom && <div className="scq-meta">from {r.chainFrom}</div>}</div>
                        <div><div className="scq-meta">Hotel</div>{r.hotelId || 'all hotels in the chain'}
                            {r.hotelFrom && <div className="scq-meta">from {r.hotelFrom}</div>}</div>
                        {r.secrets && <div><div className="scq-meta">Secrets</div>
                            {Object.entries(r.secrets).map(([k, ok]) => (
                                <span key={k} className="scq-meta" style={{ marginRight: '0.5rem', color: ok ? undefined : '#b91c1c' }}>{k} {ok ? '✓' : '✗'}</span>))}</div>}
                        {r.settingNames && <div><div className="scq-meta">Settings passed</div><span className="scq-meta">{r.settingNames.join(', ') || '—'}</span></div>}
                    </div>
                    {status.missing && status.missing.length > 0 && (
                        <div className="os-msg os-msg-error">Cannot deploy yet — missing {status.missing.join(', ')}.</div>
                    )}
                </div>
            )}

            {managed && (
                <div className="scq-form stc-settings">
                    <label>Log level
                        <select value={form.logLevel} onChange={e => setForm(f => ({ ...f, logLevel: e.target.value }))}>
                            {LOG_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </label>
                </div>
            )}

            {error && <div className="os-msg os-msg-error">{error}</div>}

            <div className="stc-actions">
                {onDeploy && (managed || (dep && status.key && !/^(pl|sp)-/.test(status.key))) && (
                    <button className="acp-btn acp-btn-primary" disabled={!!busy}
                            onClick={() => run('deploy', () => onDeploy(managed ? form : {}))}>
                        {busy === 'deploy' ? 'Deploying…' : managed ? (dep ? 'Redeploy' : 'Deploy') : 'Restart'}
                    </button>
                )}
                {onStop && dep && dep.namespace && (managed || /^(pl|sp)-/.test(status.key)) && (
                    <button className="acp-btn acp-btn-danger" disabled={!!busy} onClick={() => run('stop', onStop)}>
                        {busy === 'stop' ? 'Stopping…' : 'Stop'}
                    </button>
                )}
                {showLog && dep && (
                    <button className="acp-btn acp-btn-secondary" onClick={() => setLogOpen(o => !o)}>{logOpen ? 'Hide log' : 'Show log'}</button>
                )}
            </div>
            {managed && <div className="scq-meta">
                Deploying writes these settings — the credential&rsquo;s, as shown above — to this client&rsquo;s own secret and
                replaces the pod. OHIP allows one consumer per application key and chain: a second one is refused (4409).
            </div>}

            {logOpen && dep && <StreamingLogViewer clientKey={status.key} />}
        </div>
    );
}
